import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useEvent } from "../contexts/EventContext";
import { useSearchParams, useParams } from "react-router-dom";
import { ListItem, Group, Person } from "../types";
import { apiService } from "../services/api";
import { BrowseLists } from "../components/lists";
import { SurpriseEventBanner } from "../components/events";
import { GiftIcon, WarningIcon, XIcon } from "@phosphor-icons/react";
import { handleApiError } from "../utils/apiErrorHandler";
import "./pages.css";

const BrowseListsPage: React.FC = () => {
  const { isAuthenticated, getUserMainPerson } = useAuth();
  const { groupSlug } = useParams<{ groupSlug: string }>();
   

  const { selectedEvent, events, selectEvent } = useEvent();
  const [searchParams, setSearchParams] = useSearchParams();
  const [group, setGroup] = useState<
    (Group & { persons: Person[]; isAdmin: boolean }) | null
  >(null);
  const [allPersonsLists, setAllPersonsLists] = useState<
    Record<number, ListItem[]>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [showSpinner, setShowSpinner] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mainPerson, setMainPerson] = useState<Person | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);


  // Get specific person from URL params if provided
  const specificPersonId = searchParams.get("person")
    ? Number.parseInt(searchParams.get("person")!)
    : null;

  // Handle event selection from URL parameter
  useEffect(() => {
    const eventIdParam = searchParams.get("event");
    // Only trigger if we have an event parameter and events are loaded
    if (eventIdParam && events.length > 0) {
      const eventId = Number.parseInt(eventIdParam, 10);
      const eventExists = events.find((e) => e.id === eventId);
      if (eventExists && selectedEvent?.id !== eventId) {
        selectEvent(eventId);
        // Clean up URL parameter
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("event");
        setSearchParams(newParams);
      }
    }
  }, [searchParams.get("event"), events.length, selectedEvent?.id, selectEvent, setSearchParams]);

  useEffect(() => {
    if (isAuthenticated && groupSlug) {
      loadGroupData();
    }
  }, [isAuthenticated, groupSlug]);

  // Load mainPerson if not already loaded (for page refresh scenarios)
  useEffect(() => {
    if (isAuthenticated && !mainPerson && selectedEvent) {
      loadMainPerson();
    }
  }, [isAuthenticated, mainPerson, selectedEvent]);

  const loadMainPerson = async () => {
    try {
      const person = await getUserMainPerson();
      setMainPerson(person);
    } catch (error) {
      console.error("Failed to load main person:", error);
    }
  };

  // Delay spinner to avoid glitch effect on fast loads
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isLoading) {
      // Show spinner after 500ms delay
      timeoutId = setTimeout(() => {
        setShowSpinner(true);
      }, 500);
    } else {
      // Hide spinner immediately when loading stops
      setShowSpinner(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading]);

  const loadGroupData = async () => {
    if (!groupSlug) return;

    try {
      const groupData = await apiService.getGroupDetails(groupSlug);
      setGroup(groupData);

      // Get the main person for the current user
      const person = await getUserMainPerson();
      setMainPerson(person);


    } catch (error) {
      console.error("Failed to load group data:", error);
      setError("Errore nel caricamento dei dati del gruppo");
    }
  };

  // Get persons to show lists for - memoized to prevent unnecessary re-renders
  const personsToShow = useMemo(() => {
    return (
      group?.persons?.filter((person) => {
        if (specificPersonId) {
          // If specific person requested, show only that person
          return person.id === specificPersonId;
        } else {
          // Otherwise show all persons except the main user person
          return !mainPerson || person.id !== mainPerson.id;
        }
      }) || []
    );
  }, [group?.persons, specificPersonId, mainPerson?.id]);

  const loadAllLists = useCallback(async () => {
    if (!selectedEvent || !mainPerson) return;

    try {
      setIsLoading(true);
      setError(null);

      // Load both event lists and user's reservations in parallel
      const [eventLists, myReservations] = await Promise.all([
        apiService.getEventLists(selectedEvent.id),
        apiService.getMyReservationsForEvent(mainPerson.id, selectedEvent.id)
      ]);

      // Create a set of item IDs that the user has reserved
      const myReservedItemIds = new Set(
        myReservations.map((reservation: any) => reservation.listItem.id)
      );

      // Transform the event lists data to match the expected format
      const listsMap: Record<number, ListItem[]> = {};

      // Initialize all persons with empty arrays
      personsToShow.forEach((person) => {
        listsMap[person.id] = [];
      });

      // Populate with actual list data
      eventLists.forEach((list: any) => {
        if (list.person && list.items) {
          // Only include persons that should be shown
          if (personsToShow.some((p) => p.id === list.person.id)) {
            // Transform items to include reservation status
            const transformedItems = list.items.map((item: any) => ({
              ...item,
              isReserved: item.isReserved || myReservedItemIds.has(item.id),
              reservedByMe: myReservedItemIds.has(item.id),
              isObscured: item.isObscured || false,
            }));

            listsMap[list.person.id] = transformedItems;
          }
        }
      });

      setAllPersonsLists(listsMap);
    } catch (err: any) {
      console.error("Error loading all lists:", err);
      setError("Errore nel caricamento delle liste regalo. Riprova più tardi.");
    } finally {
      setIsLoading(false);
    }
  }, [selectedEvent?.id, personsToShow, mainPerson?.id]);

  useEffect(() => {
    if (group && personsToShow.length > 0 && selectedEvent && mainPerson) {
      loadAllLists();
    } else if (group && personsToShow.length === 0 && selectedEvent) {
      // If we have group and event but no persons to show, we're done loading (empty state)
      setIsLoading(false);
    }
    // Keep loading true in all other cases until we have complete data
  }, [
    group?.id,
    personsToShow.length,
    specificPersonId,
    mainPerson?.id,
    selectedEvent?.id,
    loadAllLists,
  ]);

  // Reload data when window regains focus (to catch updates from other tabs/pages)
  useEffect(() => {
    const handleFocus = () => {
      setRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Trigger reload when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0 && group && personsToShow.length > 0 && selectedEvent && mainPerson) {
      loadAllLists();
    }
  }, [refreshTrigger, group, personsToShow, selectedEvent, mainPerson, loadAllLists]);

  const handleReserve = useCallback(
    async (listItemId: number) => {
      if (!mainPerson) {
        alert("Persona principale non trovata.");
        return;
      }

      try {
        await apiService.reserveItem(listItemId, mainPerson.id);

        // Update the local state to reflect the reservation
        setAllPersonsLists((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((personIdStr) => {
            const personId = Number.parseInt(personIdStr);
            updated[personId] = updated[personId].map((item) =>
              item.id === listItemId
                ? {
                    ...item,
                    isReserved: true,
                    reservedByMe: true,
                    isObscured: false,
                  }
                : item
            );
          });
          return updated;
        });
      } catch (err: any) {
        handleApiError(err, setError, () => {
          if (group && personsToShow.length > 0 && selectedEvent && mainPerson) {
            loadAllLists();
          }
        });
      }
    },
    [mainPerson?.id]
  );

  const handleCancelReservation = useCallback(
    async (listItemId: number) => {
      if (!mainPerson) {
        alert("Persona principale non trovata.");
        return;
      }

      try {
        // First, get the reservation ID - we need to find it from the item
        const reservations = await apiService.getMyReservations(mainPerson.id);
        const reservation = reservations.find(
          (r: any) => r.listItemId === listItemId
        );

        if (reservation) {
          await apiService.cancelReservation(reservation.id);

          // Update the local state to reflect the cancellation
          setAllPersonsLists((prev) => {
            const updated = { ...prev };
            Object.keys(updated).forEach((personIdStr) => {
              const personId = Number.parseInt(personIdStr);
              updated[personId] = updated[personId].map((item) =>
                item.id === listItemId
                  ? {
                      ...item,
                      isReserved: false,
                      reservedByMe: false,
                      isObscured: false,
                    }
                  : item
              );
            });
            return updated;
          });
        }
      } catch (err: any) {
        handleApiError(err, setError, () => {
          if (group && personsToShow.length > 0 && selectedEvent && mainPerson) {
            loadAllLists();
          }
        });
      }
    },
    [mainPerson?.id]
  );

  // Rimuoviamo questo controllo - ora si può vedere le liste senza selezionare una persona
  // Check loading state first, before checking for empty data
  // Only show spinner after delay to avoid glitch effect
  if (isLoading && showSpinner) {
    return (
      <div className="browse-lists-page">
        <div className="loading-container">
          <p>Caricamento delle liste regalo...</p>
        </div>
      </div>
    );
  }

  // If loading but spinner not shown yet, show nothing (blank)
  if (isLoading) {
    return <div className="browse-lists-page"></div>;
  }

  // Check for empty state or multiple persons after loading is complete
  if (personsToShow.length === 0) {
    const message = specificPersonId
      ? "Persona non trovata o non hai accesso alle sue liste."
      : "Non ci sono altri membri nel gruppo con cui condividere le liste regalo.";

    return (
      <div className="browse-lists-page">
        <div className="empty-container">
          <h2>Nessuna lista disponibile</h2>
          <p>{message}</p>
          <a href={groupSlug ? `/${groupSlug}/dashboard` : "/dashboard"} className="action-button secondary">
            Torna alla dashboard
          </a>
        </div>
      </div>
    );
  }

  // Redirect if no specific person is selected (multiple persons not supported)
  if (!specificPersonId && personsToShow.length > 1) {
    return (
      <div className="browse-lists-page">
        <div className="empty-container">
          <h2>Seleziona una persona specifica</h2>
          <p>Per visualizzare le liste regalo, devi selezionare una persona specifica dalla dashboard.</p>
          <a href={groupSlug ? `/${groupSlug}/dashboard` : "/dashboard"} className="action-button primary">
            Torna alla dashboard
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="browse-lists-page">
      <div className="modern-page-header">
        <div className="header-content">
          <div className="header-text">
            <h1>
              <GiftIcon size={38} weight="fill" />
              
              {specificPersonId && personsToShow.length > 0
                ? `La lista regalo di ${personsToShow[0].name}`
                : `Sfoglia Lista Regalo`}
            </h1>
            <p>
              {specificPersonId && personsToShow.length > 0
                ? `Scopri cosa desidera ${personsToShow[0].name} e prenota il regalo perfetto`
                : "Scopri cosa desiderano gli altri membri del gruppo e prenota i regali perfetti"}
            </p>
          </div>
          <div className="header-decoration">
            <div className="decoration-circle"></div>
            <div className="decoration-circle"></div>
            <div className="decoration-circle"></div>
          </div>
        </div>
      </div>

      {error && (
        <div className="modern-error-message">
          <div className="error-content">
            <WarningIcon size={20} weight="fill" />
            <p>{error}</p>
            <button onClick={() => setError(null)} className="error-dismiss">
              <XIcon size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* Surprise Event Banner */}
      {(() => {
        return selectedEvent?.isSurprise && mainPerson && selectedEvent;
      })() && (
        <SurpriseEventBanner
          event={selectedEvent!}
          hasActiveList={false}
          isOnlyUser={false}
        />
      )}

      {groupSlug && (
        <BrowseLists
          persons={personsToShow}
          allPersonsLists={allPersonsLists}
          currentPersonId={mainPerson?.id}
          eventId={selectedEvent?.id}
          isLoading={isLoading}
          currentGroupSlug={groupSlug}
          onReserve={handleReserve}
          onCancelReservation={handleCancelReservation}
        />
      )}
    </div>
  );
};

export default BrowseListsPage;
